import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { ReactNode, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';

const UploadBox = styled('div', { shouldForwardProp: prop => prop !== 'isDragActive' && prop !== 'borderRadius' })<{
    isDragActive?: boolean;
    borderRadius?: string;
}>`
    align-items: center;
    border-radius: ${({ borderRadius }) => borderRadius || '12px'};
    border: 1px solid ${({ isDragActive, theme }) => (isDragActive ? theme.colors.primary.main : theme.colors.black[300])};
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    width: 100%;
    gap: 7px;
`;

const Dropzone = styled(UploadBox)<{ disabled?: boolean; isDragActive?: boolean; borderRadius?: string }>`
    background: ${({ isDragActive, theme }) => (isDragActive ? theme.colors.primary.bgLight : 'none')};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    outline: none !important;
    transition: background linear 0.3s;
    border: none;

    ${({ borderRadius }) => borderRadius && `border-radius: ${borderRadius};`}
    &:hover {
        background: ${theme.colors.black[100]};
    }

    ${({ disabled }) => disabled && 'opacity: 0.5; pointer-events: none;'}
`;

interface Props<T> {
    accept: DropzoneOptions['accept'];
    from?: string;
    loadingText?: ReactNode | string;
    children: ReactNode;
    dragOverContent?: ReactNode;
    onInputChange: (file: File) => Promise<T>;
    onUploaded?: (uri: T) => Promise<void>;
    style?: React.CSSProperties;
    uploadBoxBorderRadius?: string;
    dropzoneBorderRadius?: string;
}

export default function <T>({
    children,
    dragOverContent,
    onInputChange,
    accept,
    loadingText,
    onUploaded,
    from = 'advanced',
    style,
    uploadBoxBorderRadius,
    dropzoneBorderRadius,
}: Props<T>) {
    const [isUploading, setIsUploading] = useState(false);
    const { isAnonymous, loginWithRedirect } = useAuth();
    const mixpanel = useAnalytics();

    const handleDrop = async (acceptedFiles: File[]) => {
        try {
            setIsUploading(true);
            const result = await onInputChange(acceptedFiles[0]);
            await onUploaded?.(result);
        } catch (e) {
            logger.error('Error while uploading audio', { e });
        }
        setIsUploading(false);
    };

    const handleAnonymous = () => {
        mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-box', isAnonymous });
        loginWithRedirect({
            appState: { returnTo: window.location.origin + AppRoutes.PostSso + `?from=${from}` },
        });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop,
        accept,
        multiple: false,
    });

    if (isUploading) {
        return (
            <UploadBox style={style}>
                <CircularProgress size="32px" />
                {loadingText}
            </UploadBox>
        );
    }

    const showDragOverContent = isDragActive && dragOverContent;

    return (
        <UploadBox style={style} isDragActive={isDragActive} borderRadius={uploadBoxBorderRadius}>
            <Dropzone
                {...getRootProps()}
                {...(isAnonymous ? { onClick: handleAnonymous } : {})}
                isDragActive={isDragActive}
                borderRadius={dropzoneBorderRadius}>
                <input {...getInputProps()} {...(isAnonymous ? { onClick: handleAnonymous } : {})} />
                {showDragOverContent ? dragOverContent : children}
            </Dropzone>
        </UploadBox>
    );
}
